import classnames from 'classnames';
import { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from 'src/Components/Calendars/MonthCalendar/MonthCalendar.types';
import useEventNavigation from 'src/Hooks/CalendarLogic/useEventNavigation';
import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { trackStayPeriodSelected } from 'src/Services/Mixpanel/events';
import { useEventFiltersStore } from 'src/Stores/eventFiltersStore';
import { isSleepingRoomType } from 'src/types/cartography.types';
import { FALLBACK_HOUSEHOLD_COLOR, getEventVisual } from 'src/Utils/eventVisuals';

import { RoomPeriodPanel, RoomSummary } from './RoomPeriodPanel';
import styles from './StaysTimeline.module.scss';
import { Props } from './StaysTimeline.types';

const SEJOUR_TYPES: readonly EventDtoTypeEnum[] = [
  EventDtoTypeEnum.Occupation,
  EventDtoTypeEnum.Location,
];

const MAINTENANCE_TYPES: readonly EventDtoTypeEnum[] = [
  EventDtoTypeEnum.Entretien,
  EventDtoTypeEnum.Work,
  EventDtoTypeEnum.EndOfContract,
];

interface DayInfo {
  date: Dayjs;
  index: number;
  isWeekend: boolean;
  isToday: boolean;
  isPublicHoliday: boolean;
}

interface Ribbon {
  event: CalendarEvent;
  color: string;
  label: string;
  initial?: string;
  startCol: number; // 1-based inclusive
  span: number;
  /** Range of dates this ribbon covers — used by the room period selection. */
  period?: { start: Dayjs; end: Dayjs };
  /** Dashed "free" ribbon with no event behind it: no click action. */
  isFree?: boolean;
}

interface Row {
  key: string;
  label: string;
  ribbons: Ribbon[];
  emptyClickHouseholdId?: string;
  rowVariant?: 'house' | 'household' | 'maintenance' | 'orphan' | 'rooms-header' | 'room';
  /** When true, clicking a cell on this row contributes to the room period selection. */
  participatesInPeriodSelection?: boolean;
}

const isEventOnDay = (event: CalendarEvent, day: Dayjs) => {
  const start = event.startTime.startOf('day');
  const end = (event.endTime ?? event.startTime).endOf('day');
  return !day.endOf('day').isBefore(start) && !day.startOf('day').isAfter(end);
};

// Falls back on the legacy single household for responses from an API predating `households`.
const householdsOf = (event: CalendarEvent) =>
  event.households ?? (event.household ? [event.household] : []);

// Guards against a backend that occasionally serializes `rooms` as a non-array value, which would
// crash the stays view on `.forEach` / `.some` (seen on Belharra in prod, 19/10/2026).
const roomsOf = (event: CalendarEvent) => (Array.isArray(event.rooms) ? event.rooms : []);

const clampToRange = (
  event: CalendarEvent,
  rangeStart: Dayjs,
  days: DayInfo[]
): { startCol: number; span: number } | null => {
  const eventStart = event.startTime.startOf('day');
  const eventEnd = (event.endTime ?? event.startTime).startOf('day');
  const start = eventStart.isBefore(rangeStart) ? rangeStart : eventStart;
  const lastDay = days[days.length - 1].date;
  const end = eventEnd.isAfter(lastDay) ? lastDay : eventEnd;
  if (end.isBefore(days[0].date)) return null;
  if (start.isAfter(lastDay)) return null;

  const startIdx = days.findIndex(d => d.date.isSame(start, 'day'));
  const endIdx = days.findIndex(d => d.date.isSame(end, 'day'));
  if (startIdx < 0 || endIdx < 0) return null;
  return { startCol: startIdx + 2, span: endIdx - startIdx + 1 }; // +2 : first col is label
};

const StaysTimeline = ({ selectedDate, events, onEmptyCellClick }: Props) => {
  const { t, i18n } = useTranslation();
  const navigateForEvent = useEventNavigation();
  const { data: cartographyTree } = useFetchCartographyTree();
  const showRooms = useEventFiltersStore(state => state.showRooms);

  const [anchor, setAnchor] = useState<Dayjs | null>(null);
  const [period, setPeriod] = useState<{ start: Dayjs; end: Dayjs } | null>(null);

  const sleepingRooms: RoomSummary[] = useMemo(() => {
    const result: RoomSummary[] = [];
    (cartographyTree?.buildings ?? []).forEach(building => {
      building.rooms.forEach(room => {
        if (!isSleepingRoomType(room.type)) return;
        if (room.isOutOfService) return;
        result.push({ id: room.id, name: room.name, buildingName: building.name });
      });
    });
    return result;
  }, [cartographyTree]);

  const roomsSectionVisible = showRooms && sleepingRooms.length > 0;

  const selectPeriod = (
    start: Dayjs,
    end: Dayjs,
    source: 'empty_cell' | 'stay_click',
    roomId?: string
  ) => {
    const [actualStart, actualEnd] = start.isAfter(end) ? [end, start] : [start, end];
    setPeriod({ start: actualStart, end: actualEnd });
    setAnchor(null);
    trackStayPeriodSelected({
      source,
      room_id: roomId,
      nights: Math.max(1, actualEnd.diff(actualStart, 'day')),
    });
  };

  const handlePeriodCellClick = (date: Dayjs) => {
    if (!anchor) {
      setAnchor(date);
      setPeriod(null);
    } else if (anchor.isSame(date, 'day')) {
      setAnchor(null);
    } else {
      selectPeriod(anchor, date, 'empty_cell');
    }
  };

  const days: DayInfo[] = useMemo(() => {
    const start = selectedDate.startOf('month');
    const daysInMonth = selectedDate.daysInMonth();
    const today = selectedDate.startOf('day');
    const out: DayInfo[] = [];
    for (let i = 0; i < daysInMonth; i++) {
      const d = start.add(i, 'day');
      const dow = d.day();
      const isPublicHoliday =
        events?.some(
          e => e.type === EventDtoTypeEnum.PublicHoliday && e.startTime.isSame(d, 'day')
        ) ?? false;
      out.push({
        date: d,
        index: i,
        isWeekend: dow === 0 || dow === 6,
        isToday: d.isSame(today, 'day'),
        isPublicHoliday,
      });
    }
    return out;
  }, [events, selectedDate]);

  const rows: Row[] = useMemo(() => {
    if (!days.length) return [];
    const source = events ?? [];
    const rangeStart = days[0].date;

    const sejourEvents = source.filter(e => SEJOUR_TYPES.includes(e.type));
    const maintenanceEvents = source.filter(
      e => MAINTENANCE_TYPES.includes(e.type) && e.endTime && !e.startTime.isSame(e.endTime, 'day')
    );

    // Households collected from OCCUPATION events, alphabetical. A stay tied to several
    // households shows up in each of their rows.
    const householdMap = new Map<string, { id: string; name: string; color: string }>();
    let hasOrphanSejour = false;
    sejourEvents.forEach(e => {
      const eventHouseholds = householdsOf(e);
      if (eventHouseholds.length === 0) {
        hasOrphanSejour = true;
      }
      eventHouseholds.forEach(household => {
        if (!householdMap.has(household.id)) {
          householdMap.set(household.id, {
            id: household.id,
            name: household.name,
            color: household.color ?? FALLBACK_HOUSEHOLD_COLOR,
          });
        }
      });
    });
    const households = Array.from(householdMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language)
    );

    // Row "La maison" — union of OCCUPATION, then compute free windows ≥ 2 days.
    const occupations = sejourEvents.filter(e => e.type === EventDtoTypeEnum.Occupation);
    const houseRibbons: Ribbon[] = [];

    if (roomsSectionVisible) {
      // When the house exposes sleeping rooms, show how many are free each evening instead
      // of a binary "Occupée / Libre": that's the number people actually plan against.
      const freeCountByDay = days.map(d => {
        const occupiedRoomIds = new Set<string>();
        sejourEvents.forEach(e => {
          if (!isEventOnDay(e, d.date)) return;
          roomsOf(e).forEach(r => occupiedRoomIds.add(r.id));
        });
        return Math.max(0, sleepingRooms.length - occupiedRoomIds.size);
      });

      let cursor = 0;
      while (cursor < days.length) {
        let end = cursor;
        while (end + 1 < days.length && freeCountByDay[end + 1] === freeCountByDay[cursor]) {
          end++;
        }
        const count = freeCountByDay[cursor];
        const isFull = count === 0;
        houseRibbons.push({
          event: {
            localId: `house-${cursor}`,
            id: `house-${cursor}`,
            allDay: true,
            startTime: days[cursor].date,
            endTime: days[end].date,
            name: '',
            origin: 'EXTERNAL' as never,
            type: 'OTHER' as never,
            isOccurrence: false,
          } as CalendarEvent,
          color: isFull ? '#B23A48' : 'transparent',
          label: isFull
            ? t('planning.rooms.allBooked', 'Toutes les chambres prises')
            : t('planning.rooms.freeCount', {
                defaultValue: '{{count}} chambre libre le soir',
                defaultValue_other: '{{count}} chambres libres le soir',
                count,
              }),
          startCol: cursor + 2,
          span: end - cursor + 1,
          period: { start: days[cursor].date, end: days[end].date.add(1, 'day') },
          isFree: !isFull,
        });
        cursor = end + 1;
      }
    } else {
      // Fallback — no cartography (or filter off): keep the legacy "Occupée / Libre · N jours".
      const occupiedByDay = days.map(d => occupations.some(e => isEventOnDay(e, d.date)));
      let cursor = 0;
      while (cursor < days.length) {
        if (occupiedByDay[cursor]) {
          let end = cursor;
          while (end + 1 < days.length && occupiedByDay[end + 1]) end++;
          const overlapping = occupations.filter(e =>
            days.slice(cursor, end + 1).some(d => isEventOnDay(e, d.date))
          );
          const householdNames = Array.from(
            new Set(overlapping.map(e => e.household?.name).filter((n): n is string => !!n))
          );
          const label =
            householdNames.length > 0
              ? householdNames.join(' + ')
              : t('planning.stays.occupied', { defaultValue: 'Occupée' });
          const color = overlapping[0]?.household?.color ?? FALLBACK_HOUSEHOLD_COLOR;
          houseRibbons.push({
            event: overlapping[0]!,
            color,
            label,
            startCol: cursor + 2,
            span: end - cursor + 1,
          });
          cursor = end + 1;
        } else {
          let end = cursor;
          while (end + 1 < days.length && !occupiedByDay[end + 1]) end++;
          const gapLen = end - cursor + 1;
          if (gapLen >= 2) {
            houseRibbons.push({
              event: {
                localId: `free-${cursor}`,
                id: `free-${cursor}`,
                allDay: true,
                startTime: days[cursor].date,
                endTime: days[end].date,
                name: '',
                origin: 'EXTERNAL' as never,
                type: 'OTHER' as never,
                isOccurrence: false,
              } as CalendarEvent,
              color: 'transparent',
              label: t('planning.stays.free', {
                defaultValue: 'Libre · {{count}} jours',
                count: gapLen,
              }),
              startCol: cursor + 2,
              span: gapLen,
              isFree: true,
            });
          }
          cursor = end + 1;
        }
      }
    }

    const houseRow: Row = {
      key: '_house',
      label: t('planning.stays.rows.house', { defaultValue: 'La maison' }),
      ribbons: houseRibbons,
      rowVariant: 'house',
      participatesInPeriodSelection: roomsSectionVisible,
    };

    // Rows per sleeping room — hidden through the "Chambres" filter, hidden entirely when the
    // cartography has no sleeping room (then nothing to show anyway).
    const roomRows: Row[] = roomsSectionVisible
      ? sleepingRooms.map(room => {
          const ribbons: Ribbon[] = [];
          sejourEvents
            .filter(e => roomsOf(e).some(r => r.id === room.id))
            .forEach(e => {
              const range = clampToRange(e, rangeStart, days);
              if (!range) return;
              const color = e.household?.color ?? FALLBACK_HOUSEHOLD_COLOR;
              ribbons.push({
                event: e,
                color,
                label: e.name ?? e.household?.name ?? '',
                startCol: range.startCol,
                span: range.span,
                period: {
                  start: e.startTime.startOf('day'),
                  end: (e.endTime ?? e.startTime).startOf('day'),
                },
              });
            });
          return {
            key: `room-${room.id}`,
            label: room.name,
            ribbons,
            rowVariant: 'room',
            participatesInPeriodSelection: true,
          };
        })
      : [];

    const roomsHeaderRow: Row | null = roomsSectionVisible
      ? {
          key: '_rooms-header',
          label: t('planning.rooms.sectionHeader', 'Chambres'),
          ribbons: [],
          rowVariant: 'rooms-header',
        }
      : null;

    // One row per household.
    const householdRows: Row[] = households.map(h => {
      const ribbons: Ribbon[] = [];
      sejourEvents
        .filter(e => householdsOf(e).some(household => household.id === h.id))
        .forEach(e => {
          const range = clampToRange(e, rangeStart, days);
          if (!range) return;
          ribbons.push({
            event: e,
            color: h.color,
            label: e.name ?? h.name,
            initial: range.span <= 2 ? h.name.charAt(0).toUpperCase() : undefined,
            startCol: range.startCol,
            span: range.span,
          });
        });
      return {
        key: `household-${h.id}`,
        label: h.name,
        ribbons,
        emptyClickHouseholdId: h.id,
        rowVariant: 'household',
      };
    });

    // Orphan row.
    const orphanRibbons: Ribbon[] = [];
    sejourEvents
      .filter(e => householdsOf(e).length === 0)
      .forEach(e => {
        const range = clampToRange(e, rangeStart, days);
        if (!range) return;
        orphanRibbons.push({
          event: e,
          color: FALLBACK_HOUSEHOLD_COLOR,
          label: e.name,
          startCol: range.startCol,
          span: range.span,
        });
      });

    // Maintenance row.
    const maintenanceRibbons: Ribbon[] = [];
    maintenanceEvents.forEach(e => {
      const range = clampToRange(e, rangeStart, days);
      if (!range) return;
      const visual = getEventVisual(e);
      maintenanceRibbons.push({
        event: e,
        color: visual.color ?? '#8C8C8C',
        label: e.name,
        startCol: range.startCol,
        span: range.span,
      });
    });

    const orphanRow: Row | null =
      hasOrphanSejour && orphanRibbons.length > 0
        ? {
            key: '_orphan',
            label: t('planning.stays.rows.orphan', { defaultValue: 'Séjours sans foyer' }),
            ribbons: orphanRibbons,
            rowVariant: 'orphan',
          }
        : null;

    const maintenanceRow: Row | null =
      maintenanceRibbons.length > 0
        ? {
            key: '_maintenance',
            label: t('planning.stays.rows.maintenance', { defaultValue: 'Entretien & travaux' }),
            ribbons: maintenanceRibbons,
            rowVariant: 'maintenance',
          }
        : null;

    return [
      houseRow,
      roomsHeaderRow,
      ...roomRows,
      ...householdRows,
      orphanRow,
      maintenanceRow,
    ].filter((r): r is Row => !!r);
  }, [days, events, i18n.language, roomsSectionVisible, sleepingRooms, t]);

  const gridTemplateColumns = `190px repeat(${days.length}, minmax(24px, 1fr))`;

  return (
    <div className={styles.container}>
      {/* Day header */}
      <div className={styles.gridRow} style={{ gridTemplateColumns }}>
        <div className={classnames(styles.rowLabel, styles.headerLabel)}>
          {t('planning.stays.rows.house', { defaultValue: 'La maison' })}
        </div>
        {days.map(d => (
          <div
            key={d.date.format('YYYY-MM-DD')}
            className={classnames(styles.dayCell, {
              [styles.weekend]: d.isWeekend,
            })}
          >
            <span
              className={classnames(styles.dayNumber, {
                [styles.today]: d.isToday,
                [styles.ferie]: d.isPublicHoliday && !d.isToday,
              })}
            >
              {d.date.date()}
            </span>
          </div>
        ))}
      </div>

      {/* Rows */}
      {rows.map(row => {
        const isSectionHeader = row.rowVariant === 'rooms-header';
        return (
          <div
            key={row.key}
            className={classnames(styles.gridRow, {
              [styles.sectionHeader]: isSectionHeader,
            })}
            style={{ gridTemplateColumns }}
          >
            <div
              className={classnames(styles.rowLabel, {
                [styles.rowLabelHouse]: row.rowVariant === 'house',
                [styles.rowLabelSection]: isSectionHeader,
                [styles.rowLabelRoom]: row.rowVariant === 'room',
              })}
            >
              {row.label}
            </div>
            {days.map(d => {
              const isAnchor = anchor?.isSame(d.date, 'day');
              const isInPeriod =
                period && !d.date.isBefore(period.start) && d.date.isBefore(period.end);
              const isPeriodable = !!row.participatesInPeriodSelection;
              const isCreatable = !!row.emptyClickHouseholdId && !!onEmptyCellClick;
              return (
                <div
                  key={d.date.format('YYYY-MM-DD')}
                  className={classnames(styles.bodyCell, {
                    [styles.weekend]: d.isWeekend,
                    [styles.clickable]: isCreatable || isPeriodable,
                    [styles.cellAnchor]: isPeriodable && isAnchor,
                    [styles.cellInPeriod]: isPeriodable && isInPeriod,
                  })}
                  onClick={() => {
                    if (isPeriodable) {
                      handlePeriodCellClick(d.date);
                    } else if (isCreatable) {
                      onEmptyCellClick!({
                        householdId: row.emptyClickHouseholdId!,
                        date: d.date.toDate(),
                      });
                    }
                  }}
                />
              );
            })}
            {row.ribbons.map((ribbon, i) => (
              <button
                key={`${row.key}-ribbon-${i}`}
                type="button"
                className={classnames(styles.ribbon, {
                  [styles.freeRibbon]: ribbon.isFree || ribbon.color === 'transparent',
                })}
                style={{
                  gridColumn: `${ribbon.startCol} / span ${ribbon.span}`,
                  background:
                    ribbon.isFree || ribbon.color === 'transparent' ? undefined : ribbon.color,
                }}
                onClick={e => {
                  e.stopPropagation();
                  // A ribbon that carries a period belongs to the room grid: clicking it picks
                  // that period in the panel. Elsewhere, keep the legacy "open the stay" nav.
                  if (ribbon.period) {
                    const roomId =
                      row.rowVariant === 'room' ? row.key.replace('room-', '') : undefined;
                    selectPeriod(ribbon.period.start, ribbon.period.end, 'stay_click', roomId);
                  } else if (!ribbon.isFree) {
                    navigateForEvent(ribbon.event);
                  }
                }}
              >
                {ribbon.initial && <span className={styles.initial}>{ribbon.initial}</span>}
                <span className={styles.ribbonLabel}>{ribbon.label}</span>
              </button>
            ))}
          </div>
        );
      })}
      {period && (
        <div className={styles.panelWrapper}>
          <RoomPeriodPanel
            start={period.start}
            end={period.end}
            rooms={sleepingRooms}
            events={events ?? []}
            onClose={() => {
              setPeriod(null);
              setAnchor(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StaysTimeline;
